/**
 * Workspace sharing settings (Settings → Workspace → Sharing): who may publish pages to the web and create public
 * file links (members / admins only / off — "off" also stops every existing link), and whether people with edit
 * access may invite others. Admins and owners edit (core `logic/roles#canEditWorkspace`); everyone else reads.
 */
import type { SharingSettings, UpdateSharingSettingsRequest } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { canEditWorkspace } from "../logic/roles.ts";
import { DEFAULT_SETTINGS } from "../logic/sharing.ts";
import type { SharingApi } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type SharingSettingsState = {
  status: "loading" | "ready" | "error";
  settings: SharingSettings;
  canEdit: boolean;
  saving: boolean;
  /** Owners and admins: live "Anyone with the link" Files items ("N public links are active"). */
  publicFileLinks?: number;
  notice?: MessageDescriptor;
  error?: MessageDescriptor;
};

export class SharingSettingsViewModel extends ViewModel<SharingSettingsState> {
  private readonly ws: WorkspaceViewModel;
  private readonly api: SharingApi;

  constructor(ws: WorkspaceViewModel, api: SharingApi) {
    super({ status: "loading", settings: DEFAULT_SETTINGS, canEdit: false, saving: false });
    this.ws = ws;
    this.api = api;
    this.onDispose(ws.subscribe(() => this.follow()));
    this.follow();
    void this.refresh();
  }

  private follow(): void {
    const c = this.ws.state.cache;
    const me = c?.members[c.meId];
    this.update({ canEdit: me !== undefined && canEditWorkspace(me, this.ws.deps.clock.now()) });
  }

  async refresh(): Promise<void> {
    try {
      const res = await this.api.settings(this.ws.wsId);
      this.update({ status: "ready", settings: res.settings, ...(res.publicFileLinks === undefined ? {} : { publicFileLinks: res.publicFileLinks }) });
    } catch {
      this.update({ status: "error" });
    }
  }

  /** Applies at once (optimistic), rolled back with a notice when refused. */
  async set(patch: UpdateSharingSettingsRequest): Promise<void> {
    if (!this.state.canEdit) return;
    const before = this.state.settings;
    this.update({ settings: { ...before, ...patch }, saving: true });
    try {
      const res = await this.api.updateSettings(this.ws.wsId, patch);
      this.update({ settings: res.settings, saving: false, notice: msg("sharing.settings.saved"), ...(res.publicFileLinks === undefined ? {} : { publicFileLinks: res.publicFileLinks }) });
    } catch {
      this.update({ settings: before, saving: false, error: msg("sharing.error.generic") });
    }
  }

  dismissNotice(): void {
    const s = { ...this.state };
    delete s.notice;
    delete s.error;
    this.setState(s);
  }
}
